import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faBarcode, faLocationDot, faShoppingCart } from '@fortawesome/pro-light-svg-icons';
import type { BatchSummary } from '@shared/models/BatchSummary';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocatingProductBlock } from './LocatingProductBlock';

const ALLOWED_SCANNER_CHAR_REGEX = /[a-zA-Z0-9\-_.\s/\\@#$%&*+=()[\]{}|:;"'<>,?!~`]/;

interface ProductToLocate {
  id: string;
  product_id?: number;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
  batch_id?: number | null;
  batch?: BatchSummary | null;
}

interface ScanLocationSectionProps {
  onLocationScan: (locationCode: string) => void;
  onScanUnits?: (productCode: string) => void;
  onClearLocation?: () => void;
  currentLocation?: {
    id: number;
    code: string;
  } | null;
  isValidating?: boolean;
  productBeingLocated?: ProductToLocate | null;
  quantityToLocate?: number;
  onQuantityChange?: (quantity: number) => void;
  onConfirmLocate?: (batchId?: number) => void;
  isLocating?: boolean;
  cartCode?: string;
  batchSelectionCandidates?: ProductToLocate[] | null;
  selectedBatchLineId?: string | null;
  onBatchLineSelect?: (lineId: string) => void;
}

export const ScanLocationSection = ({
  onLocationScan,
  onScanUnits,
  onClearLocation,
  currentLocation,
  isValidating = false,
  productBeingLocated = null,
  quantityToLocate = 1,
  onQuantityChange = () => {},
  onConfirmLocate = () => {},
  isLocating = false,
  cartCode,
  batchSelectionCandidates = null,
  selectedBatchLineId = null,
  onBatchLineSelect,
}: ScanLocationSectionProps) => {
  const { t } = useTranslation('locationordersDetailModule');
  const [inputValue, setInputValue] = useState('');
  const [lastMessage, setLastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isEditableElement = (element: Element | null): element is HTMLElement => {
    if (!(element instanceof HTMLElement)) return false;

    const tagName = element.tagName;
    return element.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
  };

  // Auto-focus inicial
  useEffect(() => {
    if (isValidating || isLocating) return;
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isValidating, isLocating, currentLocation]);

  // Limpiar mensaje después de 3 segundos
  useEffect(() => {
    if (lastMessage) {
      const timer = setTimeout(() => setLastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastMessage]);

  const handleSubmit = useCallback(() => {
    if (!inputValue.trim()) return;
    const value = inputValue.trim();

    if (productBeingLocated && onScanUnits) {
      onScanUnits(value);
    } else {
      onLocationScan(value);
    }
    setInputValue('');
  }, [inputValue, onLocationScan, onScanUnits, productBeingLocated]);

  // Capturar teclado globalmente
  useEffect(() => {
    if (isValidating || isLocating) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const currentHasFocus = inputRef.current === document.activeElement;
      const activeElement = document.activeElement;
      const isTypingInAnotherEditableField = activeElement !== inputRef.current && isEditableElement(activeElement);

      // Respetamos cualquier campo editable ajeno al scanner para que no
      // intercepte escritura manual ni robe el foco al stepper.
      if (isTypingInAnotherEditableField) return;

      if (
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        (e.shiftKey && !(e.key.length === 1 && ALLOWED_SCANNER_CHAR_REGEX.test(e.key)))
      ) {
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setInputValue('');
        return;
      }

      if (e.key.length === 1 && ALLOWED_SCANNER_CHAR_REGEX.test(e.key)) {
        e.preventDefault();
        if (!currentHasFocus && inputRef.current) {
          inputRef.current.focus();
        }
        setInputValue((prev) => prev + e.key);
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        setInputValue((prev) => prev.slice(0, -1));
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown, true);
  }, [handleSubmit, inputValue, isLocating, isValidating]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const getPlaceholder = () => {
    if (isValidating) return t('scan.processing', { defaultValue: 'Procesando...' });
    if (productBeingLocated) return t('ui.scan_units_placeholder', { defaultValue: 'Escanear código de producto...' });
    if (currentLocation) return t('ui.scan_product_placeholder', { defaultValue: 'Escanear código de producto...' });
    return t('ui.scan_location_placeholder', { defaultValue: 'Escanear código de ubicación...' });
  };

  // Si hay un producto siendo ubicado, mostrar el bloque de confirmación
  if (productBeingLocated && currentLocation) {
    return (
      <LocatingProductBlock
        product={productBeingLocated}
        currentLocation={currentLocation}
        quantityToLocate={quantityToLocate}
        onQuantityChange={onQuantityChange}
        onConfirm={onConfirmLocate}
        onCancel={onClearLocation}
        isLocating={isLocating}
        batchSelectionCandidates={batchSelectionCandidates}
        selectedBatchLineId={selectedBatchLineId}
        onBatchLineSelect={onBatchLineSelect}
      />
    );
  }

  // Input compartido - Campo de escaneo
  const scanInput = (
    <div className="relative flex-1 min-w-0">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
        <FontAwesomeIcon icon={faBarcode} className="w-4 h-4" />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        disabled={isValidating || isLocating}
        placeholder={getPlaceholder()}
        className={`w-full pl-10 pr-4 py-2 text-base border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors h-[40px] ${
          isValidating || isLocating
            ? 'bg-muted text-muted-foreground cursor-not-allowed'
            : 'bg-background text-foreground'
        }`}
        autoComplete="off"
        spellCheck={false}
        data-testid="location-scan-input"
      />
    </div>
  );

  return (
    <div className="space-y-4 scanner-container">
      {/* Paso: escanear ubicación - Carrito a la izquierda, input a la derecha (como originalmente) */}
      {!currentLocation && (
        <div className="flex flex-col sm:flex-row gap-3">
          {cartCode && (
            <div className="flex items-stretch">
              <div className="inline-flex items-center h-[40px] bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 rounded-full border border-blue-200 dark:border-blue-800">
                <FontAwesomeIcon icon={faShoppingCart} className="w-3.5 h-3.5 mr-2" />
                <span className="font-medium text-sm whitespace-nowrap">{cartCode}</span>
              </div>
            </div>
          )}
          {scanInput}
        </div>
      )}

      {/* Paso: ubicación ya escaneada - Fila con carrito + ubicación + botón, luego input ancho completo */}
      {currentLocation && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              {cartCode && (
                <div className="inline-flex items-center h-[40px] bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 rounded-full border border-blue-200 dark:border-blue-800">
                  <FontAwesomeIcon icon={faShoppingCart} className="w-3.5 h-3.5 mr-2" />
                  <span className="font-medium text-sm whitespace-nowrap">{cartCode}</span>
                </div>
              )}
              <div className="inline-flex items-center h-[40px] bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 rounded-full border border-blue-200 dark:border-blue-800">
                <FontAwesomeIcon icon={faLocationDot} className="w-3.5 h-3.5 mr-2" />
                <span className="font-medium text-sm whitespace-nowrap">{currentLocation.code}</span>
              </div>
            </div>
            {onClearLocation && (
              <button
                type="button"
                onClick={onClearLocation}
                className="inline-flex items-center px-3 py-1.5 rounded-lg border border-card-border bg-card-bg text-complementary-text hover:bg-hover-press transition-colors text-xs sm:text-sm font-medium"
                data-testid="clear-location-button"
              >
                <FontAwesomeIcon icon={faAngleLeft} className="w-3.5 h-3.5 mr-2" />
                {t('ui.change_location_button', {
                  defaultValue: 'Cambiar ubicación',
                })}
              </button>
            )}
          </div>
          <div className="w-full">{scanInput}</div>
        </>
      )}

      {/* Feedback message - Mensaje de feedback */}
      {lastMessage && (
        <div
          className={`p-3 rounded-lg text-sm font-medium ${
            lastMessage.type === 'success'
              ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-center">
            <span className="mr-2">{lastMessage.type === 'success' ? '✅' : '❌'}</span>
            {lastMessage.text}
          </div>
        </div>
      )}
    </div>
  );
};
